import React from 'react';
import { Link } from '@oysterjs/ui/link';
import { TableHeader } from '@oysterjs/ui/table';
import { type ColumnDef } from '@oysterjs/ui/data-table';
import { Checkbox } from '@oysterjs/ui/checkbox';

import type { VisibilityConfigFieldsFragment } from '~/api/graphql';
import { VisibilityBadge } from '~/components/visibility/visibility-badge';

const uniq = <T,>(arr: T[]) => Array.from(new Set(arr));

/** Title cell with an inline visibility badge — used by every list page. */
export function TitleCell(props: { title: string; visibility?: VisibilityConfigFieldsFragment }) {
  return (
    <span
      className="flex items-center gap-1.5 truncate text-sm font-medium text-neutral-900 dark:text-white"
      title={props.title}
    >
      <span className="truncate">{props.title}</span>
      <VisibilityBadge visibility={props.visibility} variant="list" />
    </span>
  );
}

/** Plain "creator name" cell — used by styles / templates / word templates. */
export function CreatedByCell(props: { name: string }) {
  return (
    <span
      className="block truncate text-sm text-neutral-600 dark:text-neutral-400"
      title={props.name}
    >
      {props.name}
    </span>
  );
}

/** Localised long-form date (e.g. "Mar 12, 2025, 3:14 PM"). */
export function UpdatedAtCell(props: { value: Date | string | null | undefined }) {
  return (
    <span className="block truncate text-xs text-neutral-600 dark:text-neutral-400">
      {props.value &&
        new Intl.DateTimeFormat('en-US', {
          dateStyle: 'medium',
          timeStyle: 'short'
        }).format(new Date(props.value))}
    </span>
  );
}

/** Short calendar date (e.g. "3/12/2025") — used by custom forms' Created column. */
export function CreatedDateCell(props: { value: Date | string | null | undefined }) {
  return (
    <span className="block truncate text-xs text-neutral-600 dark:text-neutral-400">
      {props.value && new Date(props.value).toLocaleDateString()}
    </span>
  );
}

/**
 * Edit link cell. When `disabledTitle` is supplied (e.g. ingestion still
 * processing) the cell renders a disabled-looking span with that tooltip
 * instead of a link. Without update permission the link stays -- the slideout
 * it opens is worth reading -- but calls itself "View", so the row does not
 * promise an edit the caller cannot make.
 */
export function EditLinkCell(props: { href: string; disabledTitle?: string; canEdit?: boolean }) {
  const canEdit = props.canEdit ?? true;

  if (props.disabledTitle) {
    return (
      <span
        title={props.disabledTitle}
        className="cursor-not-allowed font-medium text-neutral-400 dark:text-neutral-500"
      >
        Edit
      </span>
    );
  }
  return (
    <Link
      href={props.href}
      onClick={(e) => e.stopPropagation()}
      className="font-medium text-primary-600 hover:text-primary-900 dark:text-primary-400 dark:hover:text-primary-300"
    >
      {canEdit ? 'Edit' : 'View'}
    </Link>
  );
}

/**
 * Returns a select-all + select-row checkbox column. Behaves consistently
 * across every library list page: indeterminate when some rows are selected,
 * checked when all visible rows are selected (and there is at least one).
 */
export function selectionColumn<
  TRow extends { id: string; title: string },
  TSortKey extends string = string
>(opts: {
  selectedSet: ReadonlySet<string>;
  setSelected: React.Dispatch<React.SetStateAction<string[]>>;
  filtered: TRow[];
  pluralLabel: string;
}): ColumnDef<TRow, TSortKey, never> {
  const { selectedSet, setSelected, filtered, pluralLabel } = opts;
  const allSelected =
    filtered.length > 0 && selectedSet.size === filtered.length && filtered.length > 0;
  const someSelected = selectedSet.size > 0 && selectedSet.size < filtered.length;
  return {
    id: 'select',
    noHref: true,
    cellClassName: 'w-0 py-1.5 px-3',
    renderHeader: () => (
      <TableHeader className="w-0 py-1.5 px-3 text-xs">
        <Checkbox
          aria-label={`Select all ${pluralLabel}`}
          checked={allSelected}
          color="sky"
          indeterminate={someSelected}
          onChange={(v) => setSelected(v ? filtered.map((row) => row.id) : [])}
        />
      </TableHeader>
    ),
    renderCell: (row) => (
      <Checkbox
        aria-label={`Select ${row.title}`}
        checked={selectedSet.has(row.id)}
        color="sky"
        onChange={(v) => {
          setSelected((prev) => (v ? uniq([...prev, row.id]) : prev.filter((id) => id !== row.id)));
        }}
      />
    )
  };
}
